import React, { createContext, useContext, useReducer, ReactNode, Dispatch, useEffect, useRef } from 'react';
import { EventData, Participant, ParticipantSettings, View, Conversation, Message } from '../types';
import { authenticatedFetch } from './auth';
import { useAuth } from './AuthContext';
import { pingApi, loadEventData as loadEventDataFromApi } from './eventApi';
import useEventPolling from './useEventPolling';

const API_URL = import.meta.env.VITE_API_URL || '';

// State interface
export interface AppState {
  eventData: EventData | null;
  loading: boolean;
  error: string | null;
  view: View;
  online?: boolean;
  lastUpdated?: string | null;
}

// Action types
export type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_EVENT_DATA'; payload: EventData }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_VIEW'; payload: View }
  | { type: 'UPDATE_PARTICIPANT'; payload: Participant }
  | { type: 'CLEAR_EVENT_DATA' }
  | { type: 'REGISTER_ARBEITSGRUPPE'; payload: { arbeitsgruppeId: number; participantId: number } }
  | { type: 'UNREGISTER_ARBEITSGRUPPE'; payload: { arbeitsgruppeId: number; participantId: number } }
  | { type: 'REGISTER_ACTIVITY_GROUP'; payload: { activityGroupId: number; participantId: number } }
  | { type: 'UNREGISTER_ACTIVITY_GROUP'; payload: { activityGroupId: number; participantId: number } }
  | { type: 'SET_CONVERSATIONS'; payload: Conversation[] }
  | { type: 'ADD_CONVERSATION'; payload: Conversation }
  | { type: 'UPDATE_CONVERSATION_LAST_MESSAGE'; payload: { conversationId: number; message: Message } }
  | { type: 'MARK_CONVERSATION_SEEN'; payload: { conversationId: number } }
  | { type: 'INCREMENT_UNSEEN_COUNT'; payload: { conversationId: number } }
  | { type: 'SET_ONLINE'; payload: boolean }
  | { type: 'SET_LAST_UPDATED'; payload: string | null }
  | { type: 'CAST_AG_VOTE'; payload: { questionId: number; arbeitsgruppeId: number } }
  | { type: 'BLOCK_PARTICIPANT'; payload: { participantId: number; userId: number } }
  | { type: 'UNBLOCK_PARTICIPANT'; payload: { participantId: number; userId: number } }
  | { type: 'UPDATE_ME_SETTINGS'; payload: ParticipantSettings };

// Initial state
const initialState: AppState = {
  eventData: null,
  loading: true,
  error: null,
  view: { name: 'home' },
  online: true,
  lastUpdated: null,
};

// Reducer function
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ONLINE':
      return { ...state, online: action.payload };
    case 'SET_LAST_UPDATED':
      return { ...state, lastUpdated: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_EVENT_DATA':
      return { ...state, eventData: action.payload, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_VIEW':
      return { ...state, view: action.payload };
    case 'UPDATE_PARTICIPANT':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          participants: state.eventData.participants.map(p =>
            p.id === action.payload.id ? action.payload : p
          ),
        },
      };
    case 'CLEAR_EVENT_DATA':
      return { ...state, eventData: null };
    case 'REGISTER_ARBEITSGRUPPE':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          me: {
            ...state.eventData.me,
            arbeitsgruppenIds: [...state.eventData.me.arbeitsgruppenIds, action.payload.arbeitsgruppeId],
          },
          arbeitsgruppen: state.eventData.arbeitsgruppen.map(ag =>
            ag.id === action.payload.arbeitsgruppeId
              ? { ...ag, currentParticipantsCount: ag.currentParticipantsCount + 1 }
              : ag
          ),
        },
      };
    case 'UNREGISTER_ARBEITSGRUPPE':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          me: {
            ...state.eventData.me,
            arbeitsgruppenIds: state.eventData.me.arbeitsgruppenIds.filter(id => id !== action.payload.arbeitsgruppeId),
          },
          arbeitsgruppen: state.eventData.arbeitsgruppen.map(ag =>
            ag.id === action.payload.arbeitsgruppeId
              ? { ...ag, currentParticipantsCount: Math.max(0, ag.currentParticipantsCount - 1) }
              : ag
          ),
        },
      };
    case 'REGISTER_ACTIVITY_GROUP':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          me: {
            ...state.eventData.me,
            activityGroupIds: [...state.eventData.me.activityGroupIds, action.payload.activityGroupId],
          },
        },
      };
    case 'UNREGISTER_ACTIVITY_GROUP':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          me: {
            ...state.eventData.me,
            activityGroupIds: state.eventData.me.activityGroupIds.filter(id => id !== action.payload.activityGroupId),
          },
        },
      };
    case 'SET_CONVERSATIONS':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          chat_conversations: action.payload,
        },
      };
    case 'ADD_CONVERSATION':
      if (!state.eventData) return state;
      // Check if conversation already exists
      const existingConv = state.eventData.chat_conversations?.find(c => c.id === action.payload.id);
      if (existingConv) return state;
      const newConversations = [action.payload, ...(state.eventData.chat_conversations || [])];
      // Sort by lastMessage.createdAt descending (most recent first)
      newConversations.sort((a, b) => {
        const aDate = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
        const bDate = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
        return bDate - aDate;
      });
      return {
        ...state,
        eventData: {
          ...state.eventData,
          chat_conversations: newConversations,
        },
      };
    case 'UPDATE_CONVERSATION_LAST_MESSAGE':
      if (!state.eventData) return state;
      const conversations = state.eventData.chat_conversations || [];
      const updatedConversations = conversations.map(conv =>
        conv.id === action.payload.conversationId
          ? { ...conv, lastMessage: action.payload.message, updatedAt: action.payload.message.createdAt }
          : conv
      );
      // Sort by lastMessage.createdAt descending (most recent first)
      updatedConversations.sort((a, b) => {
        const aDate = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
        const bDate = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
        return bDate - aDate;
      });
      return {
        ...state,
        eventData: {
          ...state.eventData,
          chat_conversations: updatedConversations,
        },
      };
    case 'MARK_CONVERSATION_SEEN':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          chat_conversations: (state.eventData.chat_conversations || []).map(conv =>
            conv.id === action.payload.conversationId
              ? { ...conv, unseenCount: 0 }
              : conv
          ),
        },
      };
    case 'INCREMENT_UNSEEN_COUNT':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          chat_conversations: (state.eventData.chat_conversations || []).map(conv =>
            conv.id === action.payload.conversationId
              ? { ...conv, unseenCount: (conv.unseenCount || 0) + 1 }
              : conv
          ),
        },
      };
    case 'CAST_AG_VOTE':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          agVotingQuestions: state.eventData.agVotingQuestions.map(question => {
            if (question.id !== action.payload.questionId) {
              return question;
            }

            const currentCounts = question.voteCounts || {};
            const previousArbeitsgruppeId = question.currentUserVote?.arbeitsgruppeId;
            const nextCounts = { ...currentCounts };

            if (previousArbeitsgruppeId && previousArbeitsgruppeId !== action.payload.arbeitsgruppeId) {
              nextCounts[previousArbeitsgruppeId] = Math.max(0, (nextCounts[previousArbeitsgruppeId] || 0) - 1);
            }

            if (previousArbeitsgruppeId !== action.payload.arbeitsgruppeId) {
              nextCounts[action.payload.arbeitsgruppeId] = (nextCounts[action.payload.arbeitsgruppeId] || 0) + 1;
            }

            return {
              ...question,
              currentUserVote: {
                questionId: question.id,
                arbeitsgruppeId: action.payload.arbeitsgruppeId,
              },
              voteCounts: nextCounts,
            };
          }),
        },
      };
    case 'BLOCK_PARTICIPANT':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          participants: state.eventData.participants.map(participant =>
            participant.id === action.payload.participantId
              ? {...participant, isBlocked: true}
              : participant
          ),
          users: state.eventData.users.map(user =>
            user.id === action.payload.userId
              ? {...user, isBlocked: true}
              : user
          ),
        },
      };
    case 'UNBLOCK_PARTICIPANT':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          participants: state.eventData.participants.map(participant =>
            participant.id === action.payload.participantId
              ? {...participant, isBlocked: false}
              : participant
          ),
          users: state.eventData.users.map(user =>
            user.id === action.payload.userId
              ? {...user, isBlocked: false}
              : user
          ),
        },
      };
    case 'UPDATE_ME_SETTINGS':
      if (!state.eventData) return state;
      return {
        ...state,
        eventData: {
          ...state.eventData,
          me: {
            ...state.eventData.me,
            Participant: {
              ...state.eventData.me.Participant,
              settings: action.payload,
            },
          },
        },
      };
    default:
      return state;
  }
}

// Contexts
const AppStateContext = createContext<AppState | undefined>(undefined);
const AppDispatchContext = createContext<Dispatch<AppAction> | undefined>(undefined);

// Provider component
export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Use auth inside provider so polling can call logout on auth errors
  const { logout, isAuthenticated } = useAuth();

  // Start polling for event data
  useEventPolling(dispatch, logout, isAuthenticated);

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
};

// Hooks
export const useAppState = (): AppState => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
};

export const useAppDispatch = (): Dispatch<AppAction> => {
  const context = useContext(AppDispatchContext);
  if (!context) {
    throw new Error('useAppDispatch must be used within an AppProvider');
  }
  return context;
};

// Action helpers exported for convenience
export const registerArbeitsgruppe = (dispatch: Dispatch<AppAction>, arbeitsgruppeId: number, participantId: number) => {
  dispatch({ type: 'REGISTER_ARBEITSGRUPPE', payload: { arbeitsgruppeId, participantId } });
};

export const unregisterArbeitsgruppe = (dispatch: Dispatch<AppAction>, arbeitsgruppeId: number, participantId: number) => {
  dispatch({ type: 'UNREGISTER_ARBEITSGRUPPE', payload: { arbeitsgruppeId, participantId } });
};

export const registerActivityGroup = (dispatch: Dispatch<AppAction>, activityGroupId: number, participantId: number) => {
  dispatch({ type: 'REGISTER_ACTIVITY_GROUP', payload: { activityGroupId, participantId } });
};

export const unregisterActivityGroup = (dispatch: Dispatch<AppAction>, activityGroupId: number, participantId: number) => {
  dispatch({ type: 'UNREGISTER_ACTIVITY_GROUP', payload: { activityGroupId, participantId } });
};

export const setConversations = (dispatch: Dispatch<AppAction>, conversations: Conversation[]) => {
  dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });
};

export const addConversation = (dispatch: Dispatch<AppAction>, conversation: Conversation) => {
  dispatch({ type: 'ADD_CONVERSATION', payload: conversation });
};

// Navigation helpers used throughout the app
export const navigateTo = (dispatch: Dispatch<AppAction>, view: View) => {
  dispatch({ type: 'SET_VIEW', payload: view });
};

export const navigateHome = (dispatch: Dispatch<AppAction>) => {
  navigateTo(dispatch, { name: 'home' });
};

export const navigateToParticipant = (dispatch: Dispatch<AppAction>, participantId: number | null) => {
  if (participantId == null) return;
  navigateTo(dispatch, { name: 'participant', id: participantId });
};

export const navigateToEditParticipant = (dispatch: Dispatch<AppAction>, participantId: number | null) => {
  if (participantId == null) return;
  navigateTo(dispatch, { name: 'editParticipant', id: participantId });
};

export const navigateToArbeitsgruppe = (dispatch: Dispatch<AppAction>, arbeitsgruppeId: number | null) => {
  if (arbeitsgruppeId == null) return;
  navigateTo(dispatch, { name: 'arbeitsgruppe', id: arbeitsgruppeId });
};

export const navigateToActivityGroup = (dispatch: Dispatch<AppAction>, activityGroupId: number | null) => {
  // Navigate to the activity groups list page instead of detail page
  navigateTo(dispatch, { name: 'activity_groups' });
};

export const navigateToChat = (dispatch: Dispatch<AppAction>, conversationId?: number, fromParticipantId?: number) => {
  navigateTo(dispatch, { name: 'chat', conversationId, fromParticipantId });
};

// Update participant helper
export const updateParticipant = (dispatch: Dispatch<AppAction>, participant: Participant) => {
  dispatch({ type: 'UPDATE_PARTICIPANT', payload: participant });
};

// Conversation action helpers
export const updateConversationLastMessage = (dispatch: Dispatch<AppAction>, conversationId: number, message: Message) => {
  dispatch({ type: 'UPDATE_CONVERSATION_LAST_MESSAGE', payload: { conversationId, message } });
};

export const markConversationSeen = (dispatch: Dispatch<AppAction>, conversationId: number) => {
  dispatch({ type: 'MARK_CONVERSATION_SEEN', payload: { conversationId } });
};

export const incrementUnseenCount = (dispatch: Dispatch<AppAction>, conversationId: number) => {
  dispatch({ type: 'INCREMENT_UNSEEN_COUNT', payload: { conversationId } });
};

export const castAgVote = (dispatch: Dispatch<AppAction>, questionId: number, arbeitsgruppeId: number) => {
  dispatch({ type: 'CAST_AG_VOTE', payload: { questionId, arbeitsgruppeId } });
};

export const blockParticipant = (dispatch: Dispatch<AppAction>, participantId: number, userId: number) => {
  dispatch({ type: 'BLOCK_PARTICIPANT', payload: { participantId, userId } });
};

export const unblockParticipant = (dispatch: Dispatch<AppAction>, participantId: number, userId: number) => {
  dispatch({ type: 'UNBLOCK_PARTICIPANT', payload: { participantId, userId } });
};

export const updateMeSettings = (dispatch: Dispatch<AppAction>, settings: ParticipantSettings) => {
  dispatch({ type: 'UPDATE_ME_SETTINGS', payload: settings });
};

// Load event data from API. Keep the function for backward compatibility but delegate to eventApi.loadEventData
export async function loadEventData(dispatch: Dispatch<AppAction>, logout: () => void, options?: { silent?: boolean }) {
  return loadEventDataFromApi(dispatch as any, logout as any, options as any);
}
